import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import apiClient from '../services/apiClient';

interface ProfileCustomDialogProps {
  open: boolean;
  onClose: () => void;
  onProfileCreated: () => void;
}

interface ProfileConfig {
  baseUrl: string;
  authBaseUrl: string;
  consumerKey: string;
  consumerSecret: string;
  nsisCode: string;
  authorityId: string;
}

interface ProfileForm {
  name: string;
  description: string;
  environment: string;
  config: ProfileConfig;
}

const ProfileCustomDialog: React.FC<ProfileCustomDialogProps> = ({
  open,
  onClose,
  onProfileCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [form, setForm] = useState<ProfileForm>({
    name: '',
    description: '',
    environment: 'custom',
    config: {
      baseUrl: '',
      authBaseUrl: '',
      consumerKey: '',
      consumerSecret: '',
      nsisCode: '202011400',
      authorityId: 'STRPRIV45'
    }
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validazione base
      if (!form.name || !form.config.baseUrl || !form.config.authBaseUrl || 
          !form.config.consumerKey || !form.config.consumerSecret) {
        setError('Tutti i campi obbligatori devono essere compilati');
        return;
      }

      // Valida URL
      try {
        new URL(form.config.baseUrl);
        new URL(form.config.authBaseUrl);
      } catch {
        setError('Formato URL non valido');
        return;
      }

      const response = await apiClient.post('/recup-profiles/custom', form);

      if (response.data.success) {
        onProfileCreated();
        onClose();
        resetForm();
      } else {
        setError(response.data.error || 'Errore durante la creazione del profilo');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      environment: 'custom',
      config: {
        baseUrl: '',
        authBaseUrl: '',
        consumerKey: '',
        consumerSecret: '',
        nsisCode: '202011400',
        authorityId: 'STRPRIV45'
      }
    });
    setError('');
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  const updateForm = (path: string, value: string) => {
    setForm(prev => {
      const newForm = { ...prev };
      const keys = path.split('.');
      let current: any = newForm;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Crea Profilo RECUP Personalizzato</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Nome Profilo *"
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              disabled={loading}
              placeholder="es: Produzione Lazio, Test Collaudo..."
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Ambiente</InputLabel>
              <Select
                value={form.environment}
                onChange={(e) => updateForm('environment', e.target.value)}
                disabled={loading}
                label="Ambiente"
              >
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="development">Development</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrizione"
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              disabled={loading}
              multiline
              rows={2}
              placeholder="Descrizione del profilo (opzionale)"
            />
          </Grid>

          {/* URLs */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Base URL API RECUP *"
              value={form.config.baseUrl}
              onChange={(e) => updateForm('config.baseUrl', e.target.value)}
              disabled={loading}
              placeholder="https://gwapi-recup.servicelazio.it/recup/1.0"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Auth Base URL *"
              value={form.config.authBaseUrl}
              onChange={(e) => updateForm('config.authBaseUrl', e.target.value)}
              disabled={loading}
              placeholder="https://iam.regione.lazio.it:443"
            />
          </Grid>

          {/* Credenziali */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Consumer Key *"
              value={form.config.consumerKey}
              onChange={(e) => updateForm('config.consumerKey', e.target.value)}
              disabled={loading}
              type="password"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Consumer Secret *"
              value={form.config.consumerSecret}
              onChange={(e) => updateForm('config.consumerSecret', e.target.value)}
              disabled={loading}
              type="password"
            />
          </Grid>

          {/* Codici */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Codice NSIS"
              value={form.config.nsisCode}
              onChange={(e) => updateForm('config.nsisCode', e.target.value)}
              disabled={loading}
              placeholder="202011400"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Authority ID"
              value={form.config.authorityId}
              onChange={(e) => updateForm('config.authorityId', e.target.value)}
              disabled={loading}
              placeholder="STRPRIV45"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Creazione...
            </Box>
          ) : (
            'Crea Profilo'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileCustomDialog;